import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import FloatingContactIcons from '../../components/feature/FloatingContactIcons';
import { api } from '../../lib/api';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  author: string;
  status: 'draft' | 'published';
  featured_image: string;
  cover_image: string;
  tags: string[];
  reading_time: number;
  seo_title: string;
  seo_description: string;
  og_image: string;
  published_at: string | null;
  created_at: string;
}

const BlogDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        // Fetch all published posts and find by slug
        const data: BlogPost[] = await api.get('/blog?status=published');
        const found = data.find(p => p.slug === slug);
        if (!found) { setNotFound(true); return; }
        // Fetch full content
        const full = await api.get(`/blog/${found.id}`);
        setPost(full);

        // SEO
        if (full.seo_title || full.title) document.title = full.seo_title || full.title;
        const setMeta = (name: string, content: string, prop = false) => {
          if (!content) return;
          const attr = prop ? 'property' : 'name';
          let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement;
          if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
          el.setAttribute('content', content);
        };
        setMeta('description', full.seo_description || full.excerpt || '');
        setMeta('og:title', full.seo_title || full.title, true);
        setMeta('og:description', full.seo_description || full.excerpt || '', true);
        if (full.og_image || full.cover_image || full.featured_image)
          setMeta('og:image', full.og_image || full.cover_image || full.featured_image, true);
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'long', day: 'numeric', year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-[#C5A46D] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Header />
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-6">
          <i className="ri-article-line text-6xl text-[#C5A46D]/30 mb-6"></i>
          <h1 className="text-3xl font-light mb-3">Post Not Found</h1>
          <p className="text-gray-400 mb-8">The article you are looking for does not exist or has been removed.</p>
          <button
            onClick={() => navigate('/blog')}
            className="px-8 py-3 bg-[#C5A46D] text-black font-medium rounded-full hover:bg-[#B8955E] transition-colors cursor-pointer"
          >
            Back to Blog
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const coverUrl = post.cover_image || post.featured_image;

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />

      {/* Cover */}
      {coverUrl && (
        <div className="relative w-full h-[50vh] md:h-[60vh] overflow-hidden">
          <img src={coverUrl} alt={post.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black" />
        </div>
      )}

      {/* Article */}
      <article className="container mx-auto px-6 max-w-3xl py-16">
        {/* Back */}
        <button
          onClick={() => navigate('/blog')}
          className="flex items-center gap-2 text-gray-400 hover:text-[#C5A46D] mb-10 text-sm cursor-pointer transition-colors group"
        >
          <i className="ri-arrow-left-line group-hover:-translate-x-1 transition-transform"></i>
          All Posts
        </button>

        {/* Meta */}
        <div className="flex flex-wrap items-center gap-3 mb-6 text-sm text-gray-400">
          <span className="flex items-center gap-1.5">
            <i className="ri-user-line text-[#C5A46D]"></i> {post.author}
          </span>
          <span className="text-white/20">•</span>
          <span className="flex items-center gap-1.5">
            <i className="ri-calendar-line text-[#C5A46D]"></i>
            {formatDate(post.published_at || post.created_at)}
          </span>
          {post.reading_time && (
            <>
              <span className="text-white/20">•</span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line text-[#C5A46D]"></i> {post.reading_time} min read
              </span>
            </>
          )}
        </div>

        {/* Title */}
        <h1 className="text-3xl md:text-5xl font-light leading-tight mb-6">
          {post.title}
        </h1>

        {/* Excerpt */}
        {post.excerpt && (
          <p className="text-xl text-gray-300 leading-relaxed mb-10 border-l-4 border-[#C5A46D] pl-5 italic">
            {post.excerpt}
          </p>
        )}

        {/* Tags */}
        {post.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-10">
            {post.tags.map(tag => (
              <span key={tag} className="px-3 py-1 bg-[#C5A46D]/10 text-[#C5A46D] rounded-full text-xs font-medium border border-[#C5A46D]/20">
                #{tag}
              </span>
            ))}
          </div>
        )}

        {/* Divider */}
        <div className="h-px bg-gradient-to-r from-[#C5A46D]/50 via-transparent to-transparent mb-10" />

        {/* Content */}
        <div
          className="prose prose-invert prose-lg max-w-none
            [&_h1]:text-4xl [&_h1]:font-light [&_h1]:mb-6 [&_h1]:text-white
            [&_h2]:text-3xl [&_h2]:font-light [&_h2]:mb-5 [&_h2]:text-white [&_h2]:mt-12
            [&_h3]:text-2xl [&_h3]:font-medium [&_h3]:mb-4 [&_h3]:text-[#C5A46D] [&_h3]:mt-8
            [&_h4]:text-xl [&_h4]:font-medium [&_h4]:mb-3 [&_h4]:text-gray-200
            [&_p]:text-gray-300 [&_p]:leading-8 [&_p]:mb-5
            [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:text-gray-300 [&_ul]:space-y-2 [&_ul]:mb-5
            [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:text-gray-300 [&_ol]:space-y-2 [&_ol]:mb-5
            [&_blockquote]:border-l-4 [&_blockquote]:border-[#C5A46D] [&_blockquote]:pl-6 [&_blockquote]:italic [&_blockquote]:text-gray-300 [&_blockquote]:my-8 [&_blockquote]:bg-white/5 [&_blockquote]:py-4 [&_blockquote]:rounded-r-lg
            [&_pre]:bg-[#1a1a22] [&_pre]:p-5 [&_pre]:rounded-xl [&_pre]:text-sm [&_pre]:overflow-x-auto [&_pre]:mb-5
            [&_code]:text-[#C5A46D] [&_code]:bg-white/10 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-sm
            [&_pre_code]:text-gray-300 [&_pre_code]:bg-transparent [&_pre_code]:p-0
            [&_a]:text-[#C5A46D] [&_a]:underline [&_a]:underline-offset-2 [&_a]:hover:text-white
            [&_img]:rounded-xl [&_img]:my-8 [&_img]:max-w-full [&_img]:shadow-lg
            [&_hr]:border-white/10 [&_hr]:my-10
            [&_strong]:text-white [&_strong]:font-semibold
            [&_em]:text-gray-200"
          dangerouslySetInnerHTML={{ __html: post.content || '' }}
        />

        {/* Bottom divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-[#C5A46D]/30 to-transparent mt-14 mb-10" />

        {/* Back to blog */}
        <div className="text-center">
          <button
            onClick={() => navigate('/blog')}
            className="inline-flex items-center gap-2 px-8 py-3 border border-[#C5A46D] text-[#C5A46D] rounded-full hover:bg-[#C5A46D] hover:text-black transition-all duration-300 cursor-pointer font-medium"
          >
            <i className="ri-arrow-left-line"></i> Back to All Posts
          </button>
        </div>
      </article>

      <Footer />
      <FloatingContactIcons />
    </div>
  );
};

export default BlogDetailPage;
